/**
 * FontAwesome Icon Wrapper Component
 *
 * This provides a consistent interface for using FontAwesome Pro icons
 * throughout the application while maintaining compatibility with existing
 * Lucide icons in ShadCN components.
 *
 * @example
 * // Import icons from your kit (Sharp family — the app default)
 * import { faHouse, faUser, faBell } from '@awesome.me/kit-e215ba4646/icons/sharp/light'
 * import { faHouse as faHouseLight } from '@awesome.me/kit-e215ba4646/icons/sharp/light'
 *
 * // Use in components
 * <Icon icon={faHouse} />
 * <Icon icon={faHouseLight} size="lg" className="text-primary" />
 * <Icon icon={faBell} size="2x" />
 */

import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome'
import { cn } from '@/lib/utils'

export interface IconProps extends Omit<FontAwesomeIconProps, 'className'> {
  className?: string
}

/**
 * Icon component wrapper for FontAwesome icons
 *
 * Sizes:
 * - 'xs' | 'sm' | 'lg' | 'xl' | '2xl'
 * - '1x' through '10x'
 *
 * Available icon styles from your kit:
 * - classic/solid (default, filled icons)
 * - classic/regular (outlined icons)
 * - classic/light (thin outlined icons)
 * - classic/thin (very thin icons)
 * - classic/brands (brand logos)
 * - sharp/solid, sharp/regular, sharp/light, sharp/thin (sharp variants)
 */
export function Icon({ className, ...props }: IconProps) {
  return <FontAwesomeIcon className={cn(className)} {...props} />
}

// Re-export FontAwesomeIcon for advanced use cases
export { FontAwesomeIcon }
